/**
 * Phase-1 sync contract: automatic operational domains vs operator-controlled
 * catalogue resync. Pure helpers — no provider I/O.
 */

export const AUTOMATIC_SYNC_DOMAINS = ["pricing", "inventory"] as const;
export const OPERATOR_SYNC_DOMAINS = [
  "content",
  "media",
  "seo",
  "shipping",
  "attributes",
  "category",
  "variants",
] as const;

export type AutomaticSyncDomain = (typeof AUTOMATIC_SYNC_DOMAINS)[number];
export type OperatorSyncDomain = (typeof OPERATOR_SYNC_DOMAINS)[number];
export type SyncDomain = AutomaticSyncDomain | OperatorSyncDomain | string;

export const DOMAIN_LABELS: Record<string, string> = {
  pricing: "Price",
  inventory: "Inventory",
  content: "Content",
  media: "Media",
  seo: "SEO",
  shipping: "Shipping",
  attributes: "Attributes",
  category: "Category",
  variants: "Variants",
};

export function isAutomaticSyncDomain(domain: string): boolean {
  return (AUTOMATIC_SYNC_DOMAINS as readonly string[]).includes(domain);
}

/**
 * Ledger-enqueued inventory_publish jobs store entity_id as the product UUID.
 * Workers must use payload.listing_id, never entity_id as a listing id.
 */
export function resolveInventoryPublishJobTarget(job: {
  entity_id?: string | null;
  payload?: { listing_id?: string | null; product_id?: string | null } | null;
}): { listingId: string; productId: string } {
  return {
    listingId: String(job.payload?.listing_id ?? "").trim(),
    productId: String(job.payload?.product_id ?? "").trim(),
  };
}

/** Live for automatic price/qty jobs: published, or already bound to a provider listing. */
export function isLiveChannelListing(opts: {
  publicationStatus?: string | null;
  externalListingId?: string | null;
  providerConfirmedProductId?: string | number | null;
}): boolean {
  if ((opts.publicationStatus ?? "") === "published") return true;
  if ((opts.externalListingId ?? "").trim() !== "") return true;
  const confirmed = opts.providerConfirmedProductId;
  return confirmed != null && String(confirmed).trim() !== "";
}

export function isOperatorSyncDomain(domain: string): boolean {
  return (OPERATOR_SYNC_DOMAINS as readonly string[]).includes(domain);
}

export function operatorDirtyDomains(dirty: string[] | null | undefined): string[] {
  return (dirty ?? []).filter(isOperatorSyncDomain);
}

export function automaticDirtyDomains(dirty: string[] | null | undefined): string[] {
  return (dirty ?? []).filter(isAutomaticSyncDomain);
}

/** Compact Products-matrix chips: Content · Variants */
export function compactOperatorDomainLabels(dirty: string[] | null | undefined): string[] {
  return operatorDirtyDomains(dirty).map((d) => DOMAIN_LABELS[d] ?? d);
}

/** Automatic price/qty confirmation may clear only these domains. */
export function domainsClearedByAutomaticConfirmation(requested: string[] | null | undefined): string[] {
  return (requested ?? []).filter(isAutomaticSyncDomain);
}

/**
 * Pre-0163 selling_price updates dirtied `variants` (and sometimes `content`)
 * instead of `pricing`. Reclassify only when history proves there was no
 * content/SKU/status change — never strip genuine manual domains.
 */
export function reclassifySellingPriceMisattributedDirty(opts: {
  dirty: string[] | null | undefined;
  hasContentFieldHistory: boolean;
  hasVariantStructureHistory: boolean;
  hasSellingPriceHistory: boolean;
}): string[] {
  const next = [...(opts.dirty ?? [])];
  if (!opts.hasSellingPriceHistory) return next;
  let touched = false;
  const drop = new Set<string>();
  if (next.includes("variants") && !opts.hasVariantStructureHistory) {
    drop.add("variants");
    touched = true;
  }
  if (next.includes("content") && !opts.hasContentFieldHistory) {
    drop.add("content");
    touched = true;
  }
  if (!touched) return next;
  const kept = next.filter((d) => !drop.has(d));
  if (!kept.includes("pricing")) kept.push("pricing");
  return kept;
}

export function shouldShowSyncChanges(dirty: string[] | null | undefined): boolean {
  return operatorDirtyDomains(dirty).length > 0;
}

export function waitingToSyncCopy(dirty: string[] | null | undefined): string {
  const ops = operatorDirtyDomains(dirty).map((d) => DOMAIN_LABELS[d] ?? d);
  if (ops.length === 0) return "";
  return `Changes waiting to sync: ${ops.join(" · ")}`;
}

export type DomainUiState =
  | "synced"
  | "needs_sync"
  | "syncing"
  | "failed"
  | "blocked";

export function domainChipLabel(domain: string, state: DomainUiState): string {
  const name = DOMAIN_LABELS[domain] ?? domain;
  switch (state) {
    case "synced":
      return `${name} ✓ Synced`;
    case "needs_sync":
      return `${name} ⚠ Needs sync`;
    case "syncing":
      return `${name} syncing...`;
    case "failed":
      return `${name} sync failed — Retry`;
    case "blocked":
      return `${name} blocked`;
  }
}

export function priceSyncUiCopy(state: "idle" | "syncing" | "synced" | "failed"): string {
  if (state === "syncing") return "Price syncing...";
  if (state === "synced") return "Price synced";
  if (state === "failed") return "Price sync failed — Retry";
  return "";
}

export function inventorySyncUiCopy(state: "idle" | "syncing" | "synced" | "failed"): string {
  if (state === "syncing") return "Inventory syncing...";
  if (state === "synced") return "Inventory synced";
  if (state === "failed") return "Inventory sync failed — Retry";
  return "";
}

/** Provider field allowlists — only these keys may be written for a domain. */
export const DOMAIN_PROVIDER_FIELDS: Record<string, { bigcommerce: string[]; ebay: string[] }> = {
  pricing: { bigcommerce: ["price", "sale_price", "retail_price"], ebay: ["price"] },
  inventory: { bigcommerce: ["inventory_level"], ebay: ["availableQuantity", "shipToLocationAvailability"] },
  content: { bigcommerce: ["name", "description"], ebay: ["title", "description"] },
  media: { bigcommerce: ["images", "is_thumbnail"], ebay: ["imageUrls"] },
  seo: { bigcommerce: ["page_title", "meta_description", "meta_keywords"], ebay: [] },
  category: { bigcommerce: ["categories"], ebay: ["categoryId"] },
  shipping: { bigcommerce: [], ebay: ["packageWeightAndSize"] },
  attributes: { bigcommerce: [], ebay: [] },
  variants: { bigcommerce: ["sku", "option_values"], ebay: [] },
};

export function assertPriceOnlyPayload(body: Record<string, unknown>): { ok: true } | { ok: false; extra: string[] } {
  const allowed = new Set(["price", "sale_price", "retail_price"]);
  const extra = Object.keys(body).filter((k) => !allowed.has(k));
  return extra.length ? { ok: false, extra } : { ok: true };
}

export function assertInventoryOnlyPayload(body: Record<string, unknown>): { ok: true } | { ok: false; extra: string[] } {
  const allowed = new Set(["inventory_level", "availableQuantity", "shipToLocationAvailability", "quantity"]);
  const extra = Object.keys(body).filter((k) => !allowed.has(k));
  return extra.length ? { ok: false, extra } : { ok: true };
}

export type InheritanceMode = "inherit" | "override";

export function resolveSyncFieldValue<T>(opts: {
  mode: InheritanceMode;
  canonical: T;
  override: T | null | undefined;
}): { write: boolean; value: T | null } {
  if (opts.mode === "override") return { write: false, value: opts.override ?? null };
  return { write: true, value: opts.canonical };
}

export function contentInheritanceMode(channelValue: string | null | undefined): InheritanceMode {
  return channelValue != null && String(channelValue).trim() !== "" ? "override" : "inherit";
}

export function mediaInheritanceMode(strategy: string | null | undefined): InheritanceMode {
  return strategy === "listing_override" ? "override" : "inherit";
}

export type ChannelPreflightRow = {
  provider: string;
  listingId: string;
  domain: string;
  outcome: "ready" | "blocked" | "skipped_override" | "unsupported";
  reason?: string;
};

export function aggregateChannelPreflight(rows: ChannelPreflightRow[]): {
  canProceed: boolean;
  blocked: ChannelPreflightRow[];
  ready: ChannelPreflightRow[];
  skipped: ChannelPreflightRow[];
} {
  const blocked = rows.filter((r) => r.outcome === "blocked");
  const ready = rows.filter((r) => r.outcome === "ready");
  const skipped = rows.filter((r) => r.outcome === "skipped_override" || r.outcome === "unsupported");
  return { canProceed: ready.length > 0 || skipped.length > 0, blocked, ready, skipped };
}

export function partialChannelSyncShouldContinue(opts: {
  succeeded: number;
  failed: number;
  blocked: number;
}): boolean {
  return opts.succeeded > 0 || opts.blocked > 0 || opts.failed > 0;
}

export function dirtyAfterPartialSync(opts: {
  before: string[];
  confirmedCleared: string[];
}): string[] {
  const cleared = new Set(opts.confirmedCleared);
  return opts.before.filter((d) => !cleared.has(d));
}

export function shouldClearDirtyAfterReadback(opts: {
  wrote: boolean;
  readbackMatches: boolean;
}): boolean {
  return opts.wrote && opts.readbackMatches;
}

/** GET-matched intended state clears dirty even when the mutation was skipped. */
export function shouldClearDirtyAfterAuthoritativeMatch(providerMatchesIntended: boolean): boolean {
  return providerMatchesIntended;
}

export type ListingSyncJobTerminal = "succeeded" | "partially_failed" | "failed";

export function listingSyncJobTerminalFromDomains(
  outcomes: Array<{ status: string }>,
): ListingSyncJobTerminal {
  const failed = outcomes.filter((o) => o.status === "failed" || o.status === "blocked");
  const progressed = outcomes.filter((o) =>
    ["synced", "skipped_override", "unsupported", "ready"].includes(o.status),
  );
  if (failed.length === 0) return "succeeded";
  if (progressed.length > 0) return "partially_failed";
  return "failed";
}

export function listingSyncHistoryBadge(
  status: string,
): "success" | "error" | "warning" | "pending" {
  if (status === "succeeded" || status === "completed") return "success";
  if (status === "failed") return "error";
  if (status === "partially_failed") return "warning";
  return "pending";
}

const CANONICAL_PRODUCT_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isCanonicalProductId(id: string | null | undefined): boolean {
  return CANONICAL_PRODUCT_ID.test((id || "").trim());
}

export function canonicalListingProductId(listing: {
  product_id?: string | null;
  product?: { id?: string | null } | Array<{ id?: string | null }> | null;
}): string | null {
  const nested = Array.isArray(listing.product) ? listing.product[0] : listing.product;
  const id = String(listing.product_id || nested?.id || "").trim();
  return isCanonicalProductId(id) ? id : null;
}

export type TrackingBoundary =
  | "local_save"
  | "provider_post"
  | "provider_response"
  | "provider_readback"
  | "local_confirmation";

export function localTrackingIsProviderProof(_tracking: string | null | undefined): boolean {
  return false;
}

export function shouldPostBcShipment(opts: {
  remoteTrackingNumbers: Array<string | null | undefined>;
  localTracking: string;
}): boolean {
  const tracking = opts.localTracking.trim();
  if (!tracking) return false;
  return !opts.remoteTrackingNumbers.some((n) => (n || "").trim() === tracking);
}

export function trackingRetryIsNoOp(opts: {
  remoteHasExactTracking: boolean;
}): boolean {
  return opts.remoteHasExactTracking;
}

export function trackingCta(opts: {
  trackingNumber: string | null | undefined;
  providerSyncStatus: string | null | undefined;
}): "record" | "retry" | "update" {
  if (!opts.trackingNumber) return "record";
  if (opts.providerSyncStatus === "error" || opts.providerSyncStatus === "pending") return "retry";
  return "update";
}

export function shouldSkipLocalTrackingRewrite(opts: {
  existingTracking: string | null | undefined;
  enteredTracking: string;
}): boolean {
  return (opts.existingTracking || "").trim() === opts.enteredTracking.trim() && opts.enteredTracking.trim() !== "";
}

export function ebayPriceOnlyRequest(opts: {
  sku: string;
  offerId: string;
  value: string;
  currency: string;
}): Record<string, unknown> {
  return {
    requests: [
      {
        sku: opts.sku,
        offers: [{ offerId: opts.offerId, price: { value: opts.value, currency: opts.currency } }],
      },
    ],
  };
}

export function bcPriceOnlyBody(price: number): Record<string, unknown> {
  return { price };
}

/** Parent catalog price for a variation product follows channel-adjusted product base. */
export function bcParentPriceOnlyPath(externalProductId: string | number): string {
  return `/catalog/products/${externalProductId}`;
}

export function bcInventoryOnlyBody(qty: number): Record<string, unknown> {
  return { inventory_level: qty };
}

export function shipmentProviderConfirmationPendingCopy(): string {
  return "Tracking saved in Tradivo. Provider confirmation pending.";
}

export function parseFunctionsInvokeError(opts: {
  errorMessage?: string | null;
  payload?: {
    error?: string;
    message?: string;
    code?: string;
    operation_id?: string;
    posted?: number;
    confirmation_pending?: boolean;
  } | null;
  surface?: "shipment" | "refund" | "generic";
}): string {
  const fromBody = opts.payload?.error || opts.payload?.message;
  if (fromBody) return fromBody;
  const msg = (opts.errorMessage || "").trim();
  const surface = opts.surface ?? "generic";
  const opStarted = Boolean(opts.payload?.operation_id)
    || opts.payload?.posted != null
    || opts.payload?.confirmation_pending === true;
  const transportFail = /non-2xx/i.test(msg) || /failed to send a request to the edge function/i.test(msg);
  if (opStarted && surface === "shipment") {
    return shipmentProviderConfirmationPendingCopy();
  }
  if (transportFail && surface === "refund") {
    return "Refund saved in Tradivo. Channel sync could not start; retry provider sync on this refund.";
  }
  if (transportFail && surface === "shipment") {
    return "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.";
  }
  if (transportFail) {
    return "Channel sync could not start. Retry.";
  }
  return msg || (surface === "shipment"
    ? "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync."
    : "Could not reach the channel service.");
}

export function shipmentTrackingSavedCopy(): string {
  return "Tracking is saved in Tradivo. Channel confirmation is shown separately.";
}

export function shipmentChannelPendingCopy(source?: string | null): string {
  if (source === "ebay") return "eBay confirmation pending.";
  if (source === "bigcommerce") return "BigCommerce confirmation pending.";
  return "Channel confirmation pending.";
}

export function retryChannelSyncLabel(): string {
  return "Retry channel sync";
}
